/**
 * Settings > Topics. Optional labels for knowledge-base articles only.
 * Tickets are organized by category, not topics.
 */

import { useState } from 'react';
import { useCreateTopic, useTopics, useUpdateTopic } from '@/lib/useServiceDesk';
import type { SdTopic } from '@/lib/serviceDeskTypes';
import { SdError } from './SdShell';
import { SD_BTN_GHOST, SD_BTN_PRIMARY, SD_CARD, SD_INPUT, SD_MUTED } from './sdStyles';

function TopicRow({ topic }: { topic: SdTopic }) {
  const update = useUpdateTopic();
  const [name, setName] = useState(topic.name);
  const dirty = name.trim() !== topic.name && name.trim().length > 0;
  return (
    <li className="flex flex-wrap items-center gap-2 py-2">
      <input
        aria-label={`Name of topic ${topic.name}`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        className={`${SD_INPUT} max-w-xs ${topic.is_active ? '' : 'opacity-60'}`}
      />
      <button
        type="button"
        disabled={!dirty || update.isPending}
        onClick={() => update.mutate({ id: topic.id, patch: { name: name.trim() } })}
        className={SD_BTN_GHOST}
      >
        Rename
      </button>
      <button
        type="button"
        disabled={update.isPending}
        onClick={() => update.mutate({ id: topic.id, patch: { is_active: !topic.is_active } })}
        className={SD_BTN_GHOST}
      >
        {topic.is_active ? 'Turn off' : 'Turn on'}
      </button>
      <span className={`text-[12px] ${SD_MUTED}`}>{topic.slug}</span>
      <SdError error={update.error} what="update the topic" />
    </li>
  );
}

export function TopicsTab() {
  const topics = useTopics();
  const create = useCreateTopic();
  const [name, setName] = useState('');

  return (
    <div className={SD_CARD}>
      <form
        className="mb-4 flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate({ name: name.trim() }, { onSuccess: () => setName('') });
        }}
      >
        <input
          aria-label="New topic name"
          placeholder="New topic"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={`${SD_INPUT} max-w-xs`}
        />
        <button type="submit" disabled={!name.trim() || create.isPending} className={SD_BTN_PRIMARY}>
          Add topic
        </button>
      </form>
      <SdError error={create.error} what="add the topic" />
      {topics.isLoading ? (
        <p className={`text-sm ${SD_MUTED}`}>Loading topics...</p>
      ) : (topics.data ?? []).length === 0 ? (
        <p className={`text-sm ${SD_MUTED}`}>No topics yet.</p>
      ) : (
        <ul className="divide-y divide-amp-line dark:divide-amp-dark-line">
          {(topics.data ?? []).map((t) => (
            <TopicRow key={t.id} topic={t} />
          ))}
        </ul>
      )}
    </div>
  );
}
